import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import CouponsList from 'components/Coupons/CouponsList';

import { couponQueryKeys } from 'lib/queryKeys';

import { getCouponsList } from 'services/coupon';
import { getStoreCouponsList } from 'services/store';

import Schema from './Schema';

import COUPONS from 'constants/coupons';
import { SORT_VALUES } from 'constants/sort';
import STORES from 'constants/store';

const FinishedCouponsList = dynamic(() => import('./FinishedCouponsList'));

const MIN_TOTAL_COUPONS = 10;

const StoreCouponsList = ({
  gaEventCategory,
  serverCoupons,
  storeId,
  storeName,
  storeSlug = null,
}) => {
  const { activeCoupons, finishedCoupons } = useMemo(() => {
    const active = [];
    const finished = [];

    for (const coupon of serverCoupons.coupons) {
      const { couponStatusName } = coupon;

      if (couponStatusName === COUPONS.STATUS.FINISHED) {
        finished.push(coupon);
      }

      if (couponStatusName === COUPONS.STATUS.APPROVED) {
        active.push(coupon);
      }
    }

    const remaining = MIN_TOTAL_COUPONS - active.length;

    return {
      activeCoupons: active,
      finishedCoupons: finished.slice(0, remaining),
    };
  }, [serverCoupons]);

  const couponsListProps = useMemo(() => {
    /**
     * Promobit coupon page is different from the rest and needs some specific
     * treatment as it is not dealt like a normal store. Most importantly, we
     * list the coupons by a different query, that takes the `couponCode`
     * instead and does a search based on that.
     */
    if (storeId === STORES.PROMOBIT_STORE_ID) {
      return {
        initialCoupons: serverCoupons,
        queryFn: ({ page = 1 }, opts) =>
          /**
           * Forces initial page to avoid possible different request results
           * between server and client
           */
          getCouponsList(
            {
              after: page,
              limit: COUPONS.COUPONS_LIMIT,
              sort: SORT_VALUES.LATEST,
              couponCodeFilter: COUPONS.PROMOBIT_COUPON_CODE,
            },
            opts
          ),
        queryKey: couponQueryKeys.listByCouponCode({
          couponCode: COUPONS.PROMOBIT_COUPON_CODE,
          sortType: SORT_VALUES.LATEST,
        }),
        withCouponStoreCover: true,
      };
    }

    return {
      initialCoupons: {
        after: serverCoupons.after,
        coupons: activeCoupons,
      },
      queryFn: ({ page, limit }, opts) =>
        getStoreCouponsList({ limit, page, storeSlug }, opts),
      queryKey: couponQueryKeys.listByStore({
        storeSlug,
        finished: false,
      }),
    };
  });

  return (
    <>
      <Schema coupons={couponsListProps.initialCoupons.coupons} />
      <CouponsList gaEventCategory={gaEventCategory} {...couponsListProps} />
      {finishedCoupons.length > 0 && storeId !== STORES.PROMOBIT_STORE_ID && (
        <FinishedCouponsList
          className="mt-12 lg:mt-16"
          coupons={finishedCoupons}
          gaEventCategory={gaEventCategory}
          storeName={storeName}
          storeSlug={storeSlug}
        />
      )}
    </>
  );
};

StoreCouponsList.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  serverCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(
      PropTypes.shape({
        couponId: PropTypes.number.isRequired,
        couponDiscountValue: PropTypes.string.isRequired,
        couponDiscountShort: PropTypes.string.isRequired,
        couponDiscount: PropTypes.string.isRequired,
        couponDiscountOn: PropTypes.string.isRequired,
        couponStatusName: PropTypes.string.isRequired,
        couponInstructions: PropTypes.string.isRequired,
        couponVerified: PropTypes.bool,
        storeImage: PropTypes.string,
        storeName: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeSlug: PropTypes.string.isRequired,
        couponTag: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        couponPublished: PropTypes.string.isRequired,
        couponTitle: PropTypes.string.isRequired,
        couponCode: PropTypes.string.isRequired,
        couponUrl: PropTypes.string.isRequired,
        userName: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  storeId: PropTypes.number.isRequired,
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string,
};

export default StoreCouponsList;
